import { useState, useCallback } from "react";
import type {
  BookingDraft,
  GuestDetails,
  PriceBreakdown,
  SelectedAddon,
} from "@/types/booking";
import type { MealPlan } from "@/types/rooms";

const today = new Date();
const tomorrow = new Date(today);
tomorrow.setDate(today.getDate() + 1);
const fmt = (d: Date) => d.toISOString().split("T")[0];

const DEFAULT_DRAFT: BookingDraft = {
  checkIn: fmt(today),
  checkOut: fmt(tomorrow),
  adults: 2,
  children: 0,
  roomTypeId: "",
  roomTypeName: "",
  mealPlan: null,
  pricing: null,
  addons: [],
  addonsTotal: 0,
  guest: null,
  razorpayOrderId: "",
};

export function useBooking() {
  const [draft, setDraft] = useState<BookingDraft>(DEFAULT_DRAFT);
  const [step, setStep] = useState(1);

  const updateDates = useCallback(
    (checkIn: string, checkOut: string, adults: number, children: number) => {
      setDraft((prev) => ({ ...prev, checkIn, checkOut, adults, children }));
    },
    [],
  );

  const selectRoom = useCallback(
    (
      roomTypeId: string,
      roomTypeName: string,
      mealPlan: MealPlan,
      pricing: PriceBreakdown,
    ) => {
      setDraft((prev) => ({
        ...prev,
        roomTypeId,
        roomTypeName,
        mealPlan,
        pricing,
        addons: [],
        addonsTotal: 0,
      }));
    },
    [],
  );

  const selectAddons = useCallback((addons: SelectedAddon[]) => {
    const addonsTotal = addons.reduce((sum, a) => sum + a.lineTotal, 0);
    setDraft((prev) => ({ ...prev, addons, addonsTotal }));
  }, []);

  const setGuestDetails = useCallback((guest: GuestDetails) => {
    setDraft((prev) => ({ ...prev, guest }));
  }, []);

  const setOrderId = useCallback((razorpayOrderId: string) => {
    setDraft((prev) => ({ ...prev, razorpayOrderId }));
  }, []);

  const goNext = useCallback(() => setStep((s) => Math.min(s + 1, 5)), []);
  const goBack = useCallback(() => setStep((s) => Math.max(s - 1, 1)), []);
  const goToStep = useCallback((s: number) => setStep(s), []);

  const reset = useCallback(() => {
    setDraft(DEFAULT_DRAFT);
    setStep(1);
  }, []);

  return {
    draft,
    step,
    updateDates,
    selectRoom,
    selectAddons,
    setGuestDetails,
    setOrderId,
    goNext,
    goBack,
    goToStep,
    reset,
  };
}